import {
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react"

import { api } from "@/api"
import { toast } from "@/hooks/use-toast"
import i18n from "@/i18n"
import { StringHelper } from "@/utils"
type DownloadMethod = "GET" | "POST"
type DownloadCSVParamsType = Record<
  string,
  string | number | boolean | null | undefined
>
type UseDownloadCSVProps = {
  endpoint: string
  fileName?: string
  params?: DownloadCSVParamsType
  body?: unknown
  method?: DownloadMethod
  withCredentials?: boolean
  addUtf8Bom?: boolean
  onSuccess?: () => void
  onError?: (err: unknown) => void
}
const buildQuery = (params?: DownloadCSVParamsType) =>
  params
    ? `?${StringHelper.objectToQueryString(params)}`
    : ""
const parseFilename = (disposition?: string) => {
  if (!disposition) return null
  const utf8Star = /filename\*=([^']*)''([^;]+)/i.exec(
    disposition
  )
  if (utf8Star?.[2]) return decodeURIComponent(utf8Star[2])
  const plain = /filename="?([^";]+)"?/i.exec(disposition)
  return plain?.[1] ?? null
}
export const useDownloadCSV = ({
  endpoint,
  fileName,
  params,
  body,
  method = "GET",
  withCredentials,
  addUtf8Bom = false,
  onSuccess,
  onError,
}: UseDownloadCSVProps) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const url = useMemo(() => {
    const suffix =
      method === "GET" ? buildQuery(params) : ""
    return `${endpoint}${suffix}`
  }, [endpoint, params, method])
  const handleDownload = useCallback(async () => {
    if (typeof window === "undefined") return
    setIsDownloading(true)
    const controller = new AbortController()
    abortRef.current = controller
    try {
      const axiosConfig = {
        responseType: "blob" as const,
        signal: controller.signal,
        withCredentials,
      }
      const res =
        method === "GET"
          ? await api.get(url, axiosConfig)
          : await api.post(
              endpoint,
              body ?? params ?? {},
              axiosConfig
            )
      if (
        !res?.data ||
        (res.data instanceof Blob && res.data.size === 0)
      ) {
        toast({
          title: i18n.t("files.emptyOrUnavailable"),
          variant: "destructive",
        })
        return
      }
      const headerName = parseFilename(
        res.headers?.["content-disposition"]
      )
      const finalName =
        headerName || fileName || "download.csv"
      let blob: Blob = res.data
      const contentType = (res.headers?.["content-type"] ||
        blob.type ||
        "text/csv") as string
      if (addUtf8Bom && contentType.includes("text/csv")) {
        const bom = new Uint8Array([0xef, 0xbb, 0xbf])
        const buf = await res.data.arrayBuffer()
        blob = new Blob([bom, new Uint8Array(buf)], {
          type: contentType,
        })
      } else if (contentType && contentType !== blob.type) {
        blob = new Blob([res.data], { type: contentType })
      }
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = objectUrl
      a.download = finalName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
      toast({
        title: i18n.t("files.downloadSuccess"),
        variant: "success",
      })
      onSuccess?.()
    } catch (err) {
      if (
        (err as Error)?.name === "CanceledError" ||
        (err as Error)?.name === "AbortError"
      ) {
        toast({ title: i18n.t("files.downloadCanceled") })
      } else {
        toast({
          title: i18n.t("files.downloadError"),
          variant: "destructive",
        })
        onError?.(err)
      }
    } finally {
      setIsDownloading(false)
      abortRef.current = null
    }
  }, [
    url,
    endpoint,
    body,
    method,
    withCredentials,
    fileName,
    addUtf8Bom,
    onSuccess,
    onError,
  ])
  const cancel = useCallback(() => {
    abortRef.current?.abort()
  }, [])
  return {
    handleDownload,
    cancel,
    isDownloading,
  }
}
export default useDownloadCSV
